
import React from "react";
import { LucideIcon } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";

export interface ActionButton {
  text: string;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost';
  icon?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  closeAfterAction?: boolean;
}

interface ActionDrawerProps {
  title: string;
  description?: string;
  actions: ActionButton[];
  isOpen: boolean;
  onClose: () => void;
  cancelText?: string;
}

const ActionDrawer = ({
  title,
  description,
  actions,
  isOpen,
  onClose,
  cancelText = "Cancel"
}: ActionDrawerProps) => {
  const handleActionClick = (action: ActionButton) => {
    action.onClick();
    if (action.closeAfterAction !== false) {
      onClose();
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className="max-h-[80vh] h-auto">
        <DrawerHeader className="text-center pb-2">
          <DrawerTitle>{title}</DrawerTitle>
          {description && (
            <DrawerDescription>{description}</DrawerDescription>
          )}
        </DrawerHeader>

        <div className="px-4 pb-4 space-y-2">
          {actions.map((action, index) => (
            <Button
              key={index}
              variant={action.variant || 'default'}
              className="w-full justify-start h-12"
              onClick={() => handleActionClick(action)}
              disabled={action.disabled}
            >
              {action.icon && <action.icon className="mr-3 h-4 w-4" />}
              {action.text}
            </Button>
          ))}
        </div>

        <DrawerFooter>
          <DrawerClose asChild>
            <Button variant="outline" className="w-full">
              {cancelText}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default ActionDrawer;
